import { Component, Injector, Input, OnDestroy, OnInit, ViewChild, DoCheck } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { AbstractDialogComponent } from '@Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { FilterService } from '@Common/Filter/services/abstract-filter.service';
import { MentionsNoFacetFilterService } from '@Common/Mentions/services/mentions-no-facet-filter.service';
import { FilterWrapperComponent } from '@Common/Filter/components/filterWrapper/filter-wrapper.component';
import { FilterSectionEnum } from '@Common/Filter/enums/filter-section.enum';
import { IncludeExcludeEnum } from '@Common/Filter/enums/include-exclude.enum';
import { FilterTypeModel } from '@Common/Filter/models/abstract-filter-type.model';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { FilterGroupModel } from '@Common/Filter/models/filter-group.model';
import { ChipModel } from '@Common/Forms/models/chip.model';
import { ActionKeys } from '@Common/Forms/enums/keys.enum';
import { SavedFilterService } from '@Common/Filter/services/saved-filter.service';
import { SavedFilter } from '@Common/Filter/models/saved-filter.model';
import { SavedFilterSetupMode } from '@Monitor/Shared/enums/saved-filter-setup-mode.enum';
import { DuplicatesStatusEnum } from '@Common/Filter/enums/duplicates-status.enum';

@Component({
    selector: 'agpr-saved-filter-setup-dialog',
    templateUrl: './saved-filter-setup-dialog.component.html',
    styleUrls: ['./saved-filter-setup-dialog.component.scss'],
    providers: [
        MentionsNoFacetFilterService,
        {
            provide: FilterService,
            useExisting: MentionsNoFacetFilterService
        }
    ],
    standalone: false
})
export class SavedFilterSetupDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy, DoCheck {

    @Input() id: string = 'savedFilterSetup';
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() mode: SavedFilterSetupMode = SavedFilterSetupMode.create;
    @Input() savedFilterSummary: SavedFilter;

    @ViewChild(FilterWrapperComponent) filterWrapper: FilterWrapperComponent;

    cachedSavedFilter: {
        currentChips: string;
        name: string;
    };

    isSavedFilterClean: boolean = true;

    filters: { [key: string]: FilterTypeModel[] } = {};
    filterSections: FilterSectionEnum[] = [];
    filtersWithoutSection: FilterTypeModel[] = [];
    flatFilters: FilterTypeModel[];
    activeFilter: FilterTypeModel;

    filterName: string = '';

    errors: { [key: string]: boolean } = {
        duplicate: false,
        empty: false,
        invalidLength: false
    };
    showErrors: boolean = false;
    allSavedFilters: SavedFilter[] = [];

    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly includeExclude = IncludeExcludeEnum;
    readonly savedFilterSetupMode = SavedFilterSetupMode;

    constructor(
        public coreDialogService: CoreDialogService,
        protected filterService: MentionsNoFacetFilterService,
        protected injector: Injector,
        private savedFilterService: SavedFilterService,
    ) {
        super(coreDialogService, 'SavedFilterSetup');
    }

    ngOnInit() {
        this.filterService.setAllFilters();
        if (this.mode === SavedFilterSetupMode.edit) {
            this.filterName = this.savedFilterSummary.name;
            this.filterService.duplicates.setValue(this.savedFilterSummary.showSimilar ? DuplicatesStatusEnum.show : DuplicatesStatusEnum.hide);
            this.filterService.applySearchQueryToFilters(this.savedFilterSummary.query);
        }

        this.subscriptions.push(
            this.filterService.getFilters().subscribe(
                (allFilters) => {
                    this.filterSections = [];
                    this.filtersWithoutSection = [];
                    for (const section of Object.values(FilterSectionEnum)) {
                        this.filters[section] = allFilters.filter((filter) => filter.section === section);

                        if (this.filters[section].length) {
                            this.filterSections.push(section);
                        }
                    }

                    this.filtersWithoutSection = allFilters.filter((filter) => !filter.section);

                    if (!this.activeFilter) {
                        if (this.filterSections.length) {
                            // default active filter is the first filter in the first section
                            this.activeFilter = this.filters[this.filterSections[0]][0];
                        } else {
                            this.activeFilter = this.filtersWithoutSection[0];
                        }
                    } else if (this.filterWrapper) {
                        this.filterWrapper.refresh();
                    }
                }
            ),
            this.filterService.getFlatFilters().subscribe(
                (flatFilters) => {
                    this.cachedSavedFilter = {
                        currentChips: JSON.stringify(flatFilters.map(i => i.currentChips || [])),
                        name: this.savedFilterSummary?.name,
                    };
                    this.flatFilters = flatFilters;
                }
            ),
            this.savedFilterService.all$.subscribe(saveFilters => {
                this.allSavedFilters = saveFilters;
            })
        );
    }

    ngDoCheck() {
        this.isSavedFilterClean = this.checkSavedFilterClean();
    }

    checkSavedFilterClean(): boolean {
        if(this.mode === SavedFilterSetupMode.edit) {
            const currentChips =  JSON.stringify(this.flatFilters.map(i => i.currentChips || []));
            return this.cachedSavedFilter?.currentChips === currentChips
                && this.cachedSavedFilter?.name === this.filterName;
        }
        return false;
    }

    apply() {
        if (!this.nameIsValid()) {
            return;
        }

        const filterToSave = this.filterService.prepareFilterModelToSave();
        filterToSave.name = this.filterName;

        if (this.mode === SavedFilterSetupMode.create) {
            this.savedFilterService.create(filterToSave).subscribe(() => {
                this.closeDialog(true);
            });
        } else if (this.mode === SavedFilterSetupMode.edit) {
            this.savedFilterService.getById(this.savedFilterSummary.id).pipe(
                switchMap(selectedFilter => {
                    selectedFilter.query = filterToSave.query;
                    selectedFilter.showSimilar = filterToSave.showSimilar;
                    selectedFilter.name = filterToSave.name;

                    return this.savedFilterService.update(selectedFilter);
                })
            ).subscribe(() => {
                this.closeDialog(true);
            });
        }
    }

    nameIsValid(): boolean {
        const trimmedFilterName = this.filterName.trim();

        if (!trimmedFilterName) {
            this.showErrors = true;
            this.errors.empty = true;
            return false;
        }

        if (!this.savedFilterService.getNameValidator().isValid(trimmedFilterName)) {
            this.showErrors = true;
            this.errors.invalidLength = true;
            return false;
        } else {
            if (this.mode === SavedFilterSetupMode.edit && this.filterName.trim().toLocaleLowerCase() === this.savedFilterSummary.name.toLocaleLowerCase()) {
                return true;
            }

            const isDuplicated = !!this.allSavedFilters.find(x => x.name.toLocaleLowerCase() === this.filterName.trim().toLocaleLowerCase());
            if (isDuplicated === true) {
                this.showErrors = true;
                this.errors.duplicate = true;
            }

            return !isDuplicated;
        }
    }


    /**
     * Removes a chip from a specified filter
     * @param filter A filter
     * @param chip A chip
     */
    removeChip(filter: FilterTypeModel, chip: ChipModel): void {
        filter.removeCurrentChip(chip);

        if (this.filterWrapper) {
            this.filterWrapper.refresh();
        }
    }

    /**
     * Selects a filter type to show the user
     * @param filter A filter type
     */
    selectFilterType(filter: FilterTypeModel, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        this.activeFilter = filter;
    }

    /**
     * Cancels all of the current filters
     */
    clear() {
        for (const section of this.filterSections) {
            this.filters[section]?.forEach((contentFilter) => {
                if (contentFilter instanceof FilterGroupModel) {
                    contentFilter.children.forEach(child => child.clear());
                } else {
                    contentFilter.clear();
                }
            });
        }
        this.filtersWithoutSection.forEach(filter => filter.clear());

        if (this.filterWrapper) {
            this.filterWrapper.refresh();
        }
    }

    cancel(visibility: boolean = false) {
        if (!visibility) {
            this.closeDialog(true);
        }
    }

    clearErrors(): void {
        this.showErrors = false;
        this.errors.duplicate = false;
        this.errors.empty = false;
        this.errors.invalidLength = false;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
